import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import React, { useEffect, useRef, useState } from "react";
import {
  DEFAULT_CONTACT_CATEGORIES,
  US_STATE_NAMES,
  type ContactCategory,
  getCategory,
  getTopic,
} from "./contactRouting";

/**
 * The contact form rendered on pivothealth.com/contact (PPI-1703).
 *
 * Replaces the legacy jQuery form in `pivot-health-com-current/contact/`. Ships
 * as one registered Builder component rather than a composition of Builder's
 * `Form:*` blocks: the conditional behavior here (sub-dropdown per category,
 * phone line per topic) would otherwise have to live as custom code inside the
 * CMS, untestable and unversioned. Only `Form:Input` and `Form:SubmitButton`
 * are SSR-patched in builderRegistry.ts anyway — `Form:Select` and
 * `Form:TextArea` still hit the raw SDK.
 *
 * Client validation here mirrors `apps/dtc-v1/lib/contactValidation.ts` so users
 * get immediate feedback; the server re-validates everything regardless. The
 * destination mailbox is never known to this component — it posts ids and the
 * server decides where the mail goes.
 */

/*
 * Styling is ported from the dtc-agent census pages so the two funnels read as
 * one product. Census is Mantine (`domains/census/components/CensusForm/
 * CensusFormTreatment.module.css` and `CensusPageContent.module.css`); this lib
 * is MUI and dtc-v1 has no Mantine provider, so the tokens are mapped by hand
 * rather than imported. Mapping, continuing the chain those files document:
 *
 *   Mantine gray.0  #F8F8F8  ($alabaster)        -> SURFACE_GREY
 *   Mantine gray.4  #CED4DA  (input border)      -> INPUT_BORDER
 *   Mantine gray.8  #303952  ($midnightBlue)     -> theme text, set below
 *   Mantine brand.5 #0086E7  ($azureRadiance)    -> dtcTheme palette.primary.main (already exact)
 *
 * Layout comes from the same census CSS: a gray card (40px radius), a centered
 * header, and the form constrained to a narrow column inside it. Census pins the
 * card at 75rem because its page also holds a wide progress bar; this form has
 * nothing else in the card, so the default is tightened to sit close to the form
 * column instead of leaving a large empty gutter. `cardMaxWidth` overrides it.
 *
 * Presentation is Builder-driven: background on/off and its color, button color
 * and label color, heading and subheading. Defaults reproduce census. Note the
 * button default is dtcTheme's site-wide orange (`backgroundColor: "#F9690E"` on
 * every MuiButton), NOT census's brand blue — on pivothealth.com a blue CTA
 * would be the only one of its kind. Set `buttonColor` to override per page.
 */
const SURFACE_GREY = "#F8F8F8";
const INPUT_BORDER = "#CED4DA";
const TEXT_DARK = "#303952";
const BRAND_BLUE = "#0086E7";
const DEFAULT_CARD_MAX_WIDTH = "46rem";
const FORM_MAX_WIDTH = "30rem";

/*
 * Input geometry taken from ps-theme (the Mantine theme census renders with):
 *   defaultRadius "md"                    -> 10px
 *   TextInput/NativeSelect minHeight      -> 44px
 *   Input.module.css :focus               -> brand-5 border + 3px 15% ring
 *
 * Mantine renders the label ABOVE the field as its own element. MUI's outlined
 * variant instead notches the label into the border, which is what made this
 * look boxy and unlike census — so the label is rendered separately here and the
 * TextField gets no `label` prop.
 */
const INPUT_RADIUS = "10px";
const INPUT_MIN_HEIGHT = "44px";
const FOCUS_RING = `0 0 0 3px ${BRAND_BLUE}26`; // 26 hex = 15% alpha

const labelSx = {
  display: "block",
  mb: "0.375rem",
  fontSize: "1rem",
  fontWeight: 500,
  color: TEXT_DARK,
} as const;

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#fff",
    borderRadius: INPUT_RADIUS,
    minHeight: INPUT_MIN_HEIGHT,
    "& fieldset": {
      borderColor: INPUT_BORDER,
      transition: "border-color 120ms",
    },
    "&:hover fieldset": { borderColor: INPUT_BORDER },
    "&.Mui-focused fieldset": { borderColor: BRAND_BLUE, borderWidth: "1px" },
    "&.Mui-focused": { boxShadow: FOCUS_RING },
  },
  "& .MuiFormHelperText-root": { marginLeft: 0 },
} as const;

export interface ContactFormProps {
  categories?: ContactCategory[];
  /** Leave empty to hide the title entirely. */
  heading?: string;
  /** Leave empty to hide the line under the title. */
  subheading?: string;
  /** Off renders the form on the page background, with no card. */
  showBackground?: boolean;
  backgroundColor?: string;
  /** Empty falls back to the theme's site-wide button color. */
  buttonColor?: string;
  buttonTextColor?: string;
  headingColor?: string;
  /** Width of the card. Ignored when the background is off. */
  cardMaxWidth?: string;
  categoryLabel?: string;
  topicLabel?: string;
  firstNameLabel?: string;
  lastNameLabel?: string;
  emailLabel?: string;
  phoneLabel?: string;
  stateLabel?: string;
  messageLabel?: string;
  submitText?: string;
  successHeading?: string;
  successMessage?: string;
  /** Prefix for the phone line, e.g. "Or call us at:". */
  phoneIntroText?: string;
}

const NAME_REGEX = /^[\p{L}\p{M}\s'.-]{1,25}$/u;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_EMAIL_LENGTH = 75;
const MAX_MESSAGE_LENGTH = 5000;
const PHONE_DIGITS = 10;

type FieldName =
  | "categoryId"
  | "topicId"
  | "firstName"
  | "lastName"
  | "email"
  | "phone"
  | "state"
  | "message";

type FieldErrors = Partial<Record<FieldName, string>>;

interface FormValues {
  categoryId: string;
  topicId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  state: string;
  message: string;
}

const EMPTY_VALUES: FormValues = {
  categoryId: "",
  topicId: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  state: "",
  message: "",
};

/**
 * Mirrors the legacy handler's getUTMParams().
 *
 * No SSR guard needed: this is only ever called from the submit handler, which
 * cannot run on the server.
 */
function readUtmParams(): Record<string, string> {
  const params = new URLSearchParams(window.location.search);
  return {
    utmSource: params.get("utm_source") ?? "",
    utmMedium: params.get("utm_medium") ?? "",
    utmCampaign: params.get("utm_campaign") ?? "",
    utmTerm: params.get("utm_term") ?? "",
    utmContent: params.get("utm_content") ?? "",
  };
}

/**
 * Pull the server's validation messages off a failed response, if it sent any.
 * Returns null when the body is missing, unparseable, or carries no messages —
 * the caller then shows its own generic copy.
 */
async function readServerErrors(response: Response): Promise<string | null> {
  try {
    const body = (await response.json()) as { errors?: unknown };
    if (Array.isArray(body.errors) && body.errors.length > 0) {
      return body.errors.filter((e) => typeof e === "string").join(" ");
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Mantine-style field: label above, plain bordered input below.
 *
 * The label is a real `<label htmlFor>` so it stays associated with the control
 * for screen readers and click-to-focus, which MUI would otherwise handle via
 * its notched `InputLabel`.
 */
function Field({
  id,
  label,
  required,
  children,
}: {
  id: string;
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Typography component="label" htmlFor={id} sx={labelSx}>
        {label}
        {required && (
          <Box component="span" sx={{ color: "error.main", ml: "0.25rem" }}>
            *
          </Box>
        )}
      </Typography>
      {children}
    </Box>
  );
}

export function validateContactForm(
  values: FormValues,
  requiresTopic: boolean
): FieldErrors {
  const errors: FieldErrors = {};

  if (!values.categoryId) errors.categoryId = "Please choose an option";
  if (requiresTopic && !values.topicId)
    errors.topicId = "Please choose a topic";

  if (!NAME_REGEX.test(values.firstName.trim())) {
    errors.firstName = "Enter a first name up to 25 letters";
  }
  if (!NAME_REGEX.test(values.lastName.trim())) {
    errors.lastName = "Enter a last name up to 25 letters";
  }

  const email = values.email.trim();
  if (!EMAIL_REGEX.test(email) || email.length > MAX_EMAIL_LENGTH) {
    errors.email = "Enter a valid email address";
  }

  const phoneDigits = values.phone.replace(/\D/g, "");
  if (values.phone.trim() && phoneDigits.length !== PHONE_DIGITS) {
    errors.phone = "Enter a 10-digit phone number";
  }

  if (!values.state) errors.state = "Please choose a state";

  const message = values.message.replace(/<[^>]*>/g, "").trim();
  if (!message) {
    errors.message = "Please enter a message";
  } else if (message.length > MAX_MESSAGE_LENGTH) {
    errors.message = "Message is too long";
  }

  return errors;
}

export default function ContactForm({
  categories = DEFAULT_CONTACT_CATEGORIES,
  heading = "Contact Us",
  subheading = "Please provide us with your contact information and your question or request here.",
  showBackground = true,
  backgroundColor = SURFACE_GREY,
  buttonColor = "",
  buttonTextColor = "",
  headingColor = TEXT_DARK,
  cardMaxWidth = DEFAULT_CARD_MAX_WIDTH,
  categoryLabel = "How can we help you?",
  topicLabel = "Please select a topic",
  firstNameLabel = "First Name",
  lastNameLabel = "Last Name",
  emailLabel = "Email",
  phoneLabel = "Phone",
  stateLabel = "State",
  messageLabel = "Message",
  submitText = "Submit",
  successHeading = "Thank You!",
  successMessage = "Your message has been sent and a representative will respond within 24-48 business hours.",
  phoneIntroText = "Or, call us at:",
}: ContactFormProps) {
  const [values, setValues] = useState<FormValues>(EMPTY_VALUES);
  const [honeypot, setHoneypot] = useState("");
  const [errors, setErrors] = useState<FieldErrors>({});
  const [apiError, setApiError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const successHeadingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (submitted && successHeadingRef.current) {
      successHeadingRef.current.focus();
    }
  }, [submitted]);

  const selectedCategory = getCategory(categories, values.categoryId);
  const selectedTopic = getTopic(selectedCategory, values.topicId);
  const hasTopics = (selectedCategory?.topics.length ?? 0) > 0;

  const setField = (field: keyof FormValues, value: string) => {
    setValues((current) => ({ ...current, [field]: value }));
  };

  const handleCategoryChange = (categoryId: string) => {
    // Reset the topic — a topic from the previous category would be unroutable.
    setValues((current) => ({ ...current, categoryId, topicId: "" }));
    setErrors({});
  };

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();

    const nextErrors = validateContactForm(values, hasTopics);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSubmitting(true);
    setApiError("");

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          categoryId: values.categoryId,
          topicId: values.topicId || undefined,
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          email: values.email.trim(),
          phone: values.phone.trim() || undefined,
          state: values.state,
          message: values.message,
          contactReference: honeypot,
          ...readUtmParams(),
        }),
      });

      if (!response.ok) {
        // Every non-ok status is surfaced verbatim when the body carries an
        // `errors` array, not just 400: the 429 ("try again in a moment") and
        // the 502 ("we could not send your message") are more useful to the
        // visitor than the generic fallback below, which is only reached when
        // the body is missing or malformed. This is safe only because
        // `pages/api/contact.ts` returns fixed, user-facing strings — a raw
        // exception message must never be put into that array.
        const serverErrors = await readServerErrors(response);
        setApiError(
          serverErrors ??
            "We could not send your message. Please try again in a moment."
        );
        return;
      }

      setSubmitted(true);
    } catch {
      setApiError(
        "Something went wrong. Please check your connection and try again."
      );
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * The census card, wrapping header and form. With the background off it drops
   * the fill, radius and padding entirely and just centers the content, so the
   * form can sit directly on a Builder section that already has its own styling.
   */
  const card = (children: React.ReactNode) => (
    <Box sx={{ py: { xs: 3, md: 6 } }} data-testid="contact-form-card">
      <Box
        sx={
          showBackground
            ? {
                maxWidth: cardMaxWidth,
                mx: "auto",
                px: 4,
                py: { xs: 5, md: 7 },
                backgroundColor,
                borderRadius: "2.5rem",
              }
            : { maxWidth: FORM_MAX_WIDTH, mx: "auto", px: 4 }
        }
      >
        {children}
      </Box>
    </Box>
  );

  if (submitted) {
    return card(
      <Stack spacing={1} sx={{ mx: "auto", textAlign: "center" }}>
        <Typography
          variant="h4"
          component="h1"
          fontWeight={700}
          color={headingColor}
          tabIndex={-1}
          ref={successHeadingRef}
          data-testid="contact-form-success-title"
        >
          {successHeading}
        </Typography>
        <Typography
          color="text.secondary"
          fontSize="1.125rem"
          data-testid="contact-form-success-message"
        >
          {successMessage}
        </Typography>
      </Stack>
    );
  }

  return card(
    <>
      {/* Census header column: narrower than the card, centered. */}
      <Stack
        spacing={1}
        sx={{ mx: "auto", mb: { xs: 4, md: 6 }, textAlign: "center" }}
      >
        {heading && (
          <Typography
            variant="h4"
            component="h1"
            fontWeight={700}
            color={headingColor}
            data-testid="contact-form-title"
          >
            {heading}
          </Typography>
        )}
        {subheading && (
          <Typography
            color="text.secondary"
            fontSize="1.125rem"
            data-testid="contact-form-subtitle"
          >
            {subheading}
          </Typography>
        )}
      </Stack>

      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        aria-busy={submitting}
        sx={{ maxWidth: FORM_MAX_WIDTH, mx: "auto" }}
        data-testid="contact-form"
      >
        <Stack spacing={2}>
          {/*
          Live region stays mounted; only its children toggle. A role="alert"
          node inserted into the DOM is announced unreliably.

          No role="alert" on the wrapper: MUI's Alert already renders one, and
          per docs/guidelines.md nesting two creates conflicting live regions
          across browsers. (AgentAuthSignIn does nest them — following the
          guideline rather than that precedent.)
        */}
          <Box aria-live="assertive" aria-atomic="true">
            {apiError && (
              <Alert severity="error" data-testid="contact-form-api-error">
                {apiError}
              </Alert>
            )}
          </Box>

          <Field id="contact-category" label={categoryLabel} required>
            <TextField
              select
              fullWidth
              id="contact-category"
              value={values.categoryId}
              onChange={(event) => handleCategoryChange(event.target.value)}
              error={Boolean(errors.categoryId)}
              helperText={errors.categoryId}
              sx={fieldSx}
              SelectProps={{
                native: true,
                inputProps: { "data-testid": "contact-form-category-select" },
              }}
            >
              <option value="">-- Select --</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.label}
                </option>
              ))}
            </TextField>
          </Field>

          {hasTopics && (
            <Field id="contact-topic" label={topicLabel} required>
              <TextField
                select
                fullWidth
                id="contact-topic"
                value={values.topicId}
                onChange={(event) => setField("topicId", event.target.value)}
                error={Boolean(errors.topicId)}
                helperText={errors.topicId}
                sx={fieldSx}
                SelectProps={{
                  native: true,
                  inputProps: { "data-testid": "contact-form-topic-select" },
                }}
              >
                <option value="">-- Select --</option>
                {selectedCategory?.topics.map((topic) => (
                  <option key={topic.id} value={topic.id}>
                    {topic.label}
                  </option>
                ))}
              </TextField>
            </Field>
          )}

          {selectedCategory?.staticParagraph && (
            <Typography
              color={TEXT_DARK}
              data-testid="contact-form-static-paragraph"
            >
              {selectedCategory.staticParagraph}
            </Typography>
          )}

          {selectedTopic?.phone && (
            <Typography
              color={TEXT_DARK}
              data-testid="contact-form-topic-phone"
            >
              {phoneIntroText}{" "}
              <Box component="span" fontWeight={700} whiteSpace="nowrap">
                {selectedTopic.phone}
              </Box>
            </Typography>
          )}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Box flex={1}>
              <Field id="contact-first-name" label={firstNameLabel} required>
                <TextField
                  fullWidth
                  id="contact-first-name"
                  value={values.firstName}
                  onChange={(event) =>
                    setField("firstName", event.target.value)
                  }
                  error={Boolean(errors.firstName)}
                  helperText={errors.firstName}
                  sx={fieldSx}
                  inputProps={{
                    maxLength: 25,
                    "data-testid": "contact-form-first-name-input",
                  }}
                />
              </Field>
            </Box>
            <Box flex={1}>
              <Field id="contact-last-name" label={lastNameLabel} required>
                <TextField
                  fullWidth
                  id="contact-last-name"
                  value={values.lastName}
                  onChange={(event) => setField("lastName", event.target.value)}
                  error={Boolean(errors.lastName)}
                  helperText={errors.lastName}
                  sx={fieldSx}
                  inputProps={{
                    maxLength: 25,
                    "data-testid": "contact-form-last-name-input",
                  }}
                />
              </Field>
            </Box>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Box flex={1}>
              <Field id="contact-email" label={emailLabel} required>
                <TextField
                  fullWidth
                  type="email"
                  id="contact-email"
                  value={values.email}
                  onChange={(event) => setField("email", event.target.value)}
                  error={Boolean(errors.email)}
                  helperText={errors.email}
                  sx={fieldSx}
                  inputProps={{
                    maxLength: MAX_EMAIL_LENGTH,
                    "data-testid": "contact-form-email-input",
                  }}
                />
              </Field>
            </Box>
            <Box flex={1}>
              <Field id="contact-phone" label={phoneLabel}>
                <TextField
                  fullWidth
                  type="tel"
                  id="contact-phone"
                  value={values.phone}
                  onChange={(event) => setField("phone", event.target.value)}
                  error={Boolean(errors.phone)}
                  helperText={errors.phone}
                  sx={fieldSx}
                  inputProps={{ "data-testid": "contact-form-phone-input" }}
                />
              </Field>
            </Box>
          </Stack>

          <Field id="contact-state" label={stateLabel} required>
            <TextField
              select
              fullWidth
              id="contact-state"
              value={values.state}
              onChange={(event) => setField("state", event.target.value)}
              error={Boolean(errors.state)}
              helperText={errors.state}
              sx={fieldSx}
              SelectProps={{
                native: true,
                inputProps: { "data-testid": "contact-form-state-select" },
              }}
            >
              <option value="">-- Select --</option>
              {US_STATE_NAMES.map((stateName) => (
                <option key={stateName} value={stateName}>
                  {stateName}
                </option>
              ))}
            </TextField>
          </Field>

          <Field id="contact-message" label={messageLabel} required>
            <TextField
              fullWidth
              multiline
              minRows={5}
              id="contact-message"
              value={values.message}
              onChange={(event) => setField("message", event.target.value)}
              error={Boolean(errors.message)}
              helperText={errors.message}
              sx={fieldSx}
              inputProps={{
                maxLength: MAX_MESSAGE_LENGTH,
                "data-testid": "contact-form-message-input",
              }}
            />
          </Field>

          {/*
            Honeypot. Off-screen rather than display:none — some bots skip
            fields that are not rendered at all but will happily fill this one.
            Real users never reach it: out of the tab order, hidden from
            assistive tech.

            The name deliberately avoids `company`/`organization`/`address` and
            anything else browsers autofill. `autocomplete="off"` is widely
            ignored, and a false positive here would silently bin a real
            member's message behind a success screen.
          */}
          <Box
            sx={{
              position: "absolute",
              left: "-9999px",
              width: "1px",
              height: "1px",
              overflow: "hidden",
            }}
            aria-hidden="true"
          >
            <label htmlFor="contact-reference">Leave this field blank</label>
            <input
              id="contact-reference"
              name="contactReference"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={(event) => setHoneypot(event.target.value)}
              data-testid="contact-form-honeypot"
            />
          </Box>

          <Box>
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={submitting}
              sx={{
                borderRadius: "0.5rem",
                py: "0.875rem",
                fontSize: "1.125rem",
                // Empty props fall through to dtcTheme's site-wide button styling.
                ...(buttonColor && {
                  backgroundColor: buttonColor,
                  "&:hover": {
                    backgroundColor: buttonColor,
                    filter: "brightness(0.92)",
                  },
                }),
                ...(buttonTextColor && { color: buttonTextColor }),
              }}
              data-testid="contact-form-submit-button"
            >
              {submitting ? (
                <CircularProgress
                  size={24}
                  data-testid="contact-form-spinner"
                />
              ) : (
                submitText
              )}
            </Button>
          </Box>
        </Stack>
      </Box>
    </>
  );
}
